import { Divider, styled, Tooltip, useTheme, Grid } from '@mui/material';
import { createShareSplitAddRoute } from 'adminConstants/routes';
import Button from 'components/Button';
import GridContainer from 'components/GridContainer';
import { NavLink } from 'react-router-dom';
import { InfoOutlined } from '@mui/icons-material';
import PageSection from 'components/PageSection';
import Heading from 'components/Heading';
import { useState } from 'react';
import { useUploadAllocations } from 'hooks/data/allocation/useUploadAllocations';
import ErrorsTable from './ErrorsTable';
import { Api } from 'further-types/allocations';
import { FundAllocation } from '.';
import ButtonAsText from 'components/ButtonAsText';
import { StyledNote } from './shared/StyledComponents';
import { Row } from 'components/Layout';

const StyledUploadButton = styled(Button)(({ theme }) => ({
  width: theme.spacing(33.75),
}));

const StyledShareSplitWarning = styled('span')(({ theme }) => ({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  letterSpacing: 0,
  marginBottom: theme.spacing(6),
  textDecoration: 'underline',
  textDecorationStyle: 'dotted',
  textDecorationColor: theme.palette.text.greyLabel,
  textUnderlineOffset: theme.spacing(0.5),
  width: 'fit-content',

  '& svg': {
    marginLeft: theme.spacing(1.5),
  },
}));

export const StyledDivider = styled(Divider)(({ theme }) => ({
  marginBottom: theme.spacing(8),
  marginTop: theme.spacing(2),
}));

const StyledButtonRow = styled(Row)(({ theme }) => ({
  marginBottom: theme.spacing(6),
}));

interface UploadAllocationSheetProps {
  onTemplateReset: () => void;
  selectedCompanyId: string;
  isNewShareClassCreated: boolean;
  onReview: () => void;
  setFundAutoAllocationResults: (
    fundAutoAllocationResults: Array<FundAllocation>,
  ) => void;
  handleClearAndRestart: () => void;
}

export interface Flag {
  uploadFlag: boolean;
  reviewFlag: boolean;
  shareAllocationUploadId: string;
  currentSharePrice: number;
  totalShareAllocate: number;
  totalSubscriptionAmount: number;
  totalTransactionFee: number;
  investorsCount: number;
  fullyDeployedTranches: Array<string>;
  selectedCompanyId: string;
}

const UploadAllocationSheet: React.FC<UploadAllocationSheetProps> = ({
  onTemplateReset,
  selectedCompanyId,
  isNewShareClassCreated,
  onReview,
  setFundAutoAllocationResults,
  handleClearAndRestart,
}) => {
  const theme = useTheme();

  const [flag, setFlag] = useState<Flag>({
    uploadFlag: true,
    reviewFlag: false,
    shareAllocationUploadId: '',
    currentSharePrice: 0,
    totalShareAllocate: 0,
    totalSubscriptionAmount: 0,
    totalTransactionFee: 0,
    investorsCount: 0,
    fullyDeployedTranches: [],
    selectedCompanyId: '',
  });

  const [uploadErrors, setUploadErrors] = useState<Array<Api.RowError>>([]);
  const { uploadFile } = useUploadAllocations();

  // Allows for the same file (edited) to be uploaded and count as onChange
  const resetFileInput = (event) => {
    const { target = {} } = event || {};
    target.value = '';
  };

  const handleSubmitUpload = async (event) => {
    setUploadErrors([]);

    setFlag({ ...flag, reviewFlag: false });

    if (event.target.files?.length) {
      try {
        const response = await uploadFile.mutateAsync(event.target.files[0]);

        if (response.error?.length) {
          setUploadErrors(response.error);
        } else {
          setFundAutoAllocationResults(response.allocationPreviews);
          onReview();
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
    }

    resetFileInput(event);
  };

  return (
    <PageSection>
      <StyledDivider />

      <Heading variant="h2">Upload your allocation sheet</Heading>

      <GridContainer>
        <Grid size={{ md: 9 }}>
          <StyledNote>
            Please check your download folder for your allocation sheet. Please
            fill in the share quantities and upload the sheet here. Form didn't
            download?{' '}
            <ButtonAsText
              variant="text"
              color="primary"
              onClick={() => {
                setFlag({
                  ...flag,
                  uploadFlag: true,
                });

                onTemplateReset();
              }}
            >
              Click here to try again
            </ButtonAsText>
            .
          </StyledNote>

          {!isNewShareClassCreated && (
            <Tooltip
              leaveDelay={250}
              title={
                <span>
                  If there has been a share split since the company's last share
                  price update, please process this in the share split function
                  for this company{' '}
                  <NavLink
                    style={{ color: theme.palette.primary[400] }}
                    target="_blank"
                    to={createShareSplitAddRoute(selectedCompanyId)}
                  >
                    here
                  </NavLink>{' '}
                  before uploading the new allocation.
                </span>
              }
            >
              <StyledShareSplitWarning>
                Has a share split taken place for this company?
                <InfoOutlined style={{ fontSize: 18 }} />
              </StyledShareSplitWarning>
            </Tooltip>
          )}

          <StyledButtonRow justify="space-between">
            <Button
              variant="outlined"
              component="label"
              color="primary"
              onClick={handleClearAndRestart}
            >
              Back
            </Button>
            <StyledUploadButton
              variant="outlined"
              component="label"
              color="primary"
              loading={uploadFile.isPending}
              onChange={handleSubmitUpload}
            >
              Upload
              <input name="uploadExcelFile" type="file" accept=".xlsx" hidden />
            </StyledUploadButton>
          </StyledButtonRow>

          {uploadErrors.length > 0 && <ErrorsTable errors={uploadErrors} />}
        </Grid>
      </GridContainer>
    </PageSection>
  );
};

export default UploadAllocationSheet;
